import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { ContentService } from '@/services/content.service'
import { useLocaleStore } from '@/stores/locale.store'
import { useUIStore } from '@/stores/ui.store'
import type { Locale } from '@/router/locales'

import page from '@/models/page'
import post from '@/models/post'
import attachment from '@/models/attachment'
import taxonomy from '@/models/taxonomy'

import cpt from '@/models/cpt'

type PageModel = ReturnType<typeof page>
type PostModel = ReturnType<typeof post>
type AttachmentModel = ReturnType<typeof attachment>
type TaxonomyModel = ReturnType<typeof taxonomy>

type CptModel = ReturnType<typeof cpt>

type ResourceKey =
  | 'pages'
  | 'posts'
  | 'attachments'
  | 'taxonomies'
  | 'faq'
  | 'references'

type CacheMeta = {
  fetchedAt: number
  ttlMs: number
  locale: Locale | null
}

const now = () => Date.now()

export const useContentStore = defineStore('content', () => {
  const localeStore = useLocaleStore()
  const ui = useUIStore()
  // Data
  const pages = ref<PageModel[]>([])
  const posts = ref<PostModel[]>([])
  const attachments = ref<AttachmentModel[]>([])
  const taxonomies = ref<TaxonomyModel[]>([])

  const faq = ref<CptModel[]>([])
  const references = ref<CptModel[]>([])

  // Status
  // isLoading = global/fullscreen preloader
  const isLoading = ref(false)

  // loading[key] = local loaders per resource
  const loading = ref<Partial<Record<ResourceKey, boolean>>>({})

  const errors = ref<Partial<Record<ResourceKey, unknown>>>({})

  const hasErrors = computed(() => Object.values(errors.value).some((e) => e != null))

  function hasActiveErrors() {
    return Object.values(errors.value).some((e) => e != null)
  }

  // ---- Cache metadata per resource
  const cache = ref<Record<ResourceKey, CacheMeta>>({
    pages: { fetchedAt: 0, ttlMs: 5 * 60_000, locale: null },
    posts: { fetchedAt: 0, ttlMs: 5 * 60_000, locale: null },
    attachments: { fetchedAt: 0, ttlMs: 10 * 60_000, locale: null },
    taxonomies: { fetchedAt: 0, ttlMs: 60 * 60_000, locale: null },

    faq: { fetchedAt: 0, ttlMs: 10 * 60_000, locale: null },
    references: { fetchedAt: 0, ttlMs: 10 * 60_000, locale: null },
  })

  // ---- In-flight promise per resource (prevents duplicate loads)
  const inFlight = new Map<ResourceKey, Promise<void>>()

  function isFresh(key: ResourceKey) {
    const meta = cache.value[key]
    return (
      meta.fetchedAt > 0
      && meta.locale === localeStore.currentLocale
      && now() - meta.fetchedAt < meta.ttlMs
    )
  }

  function markFetched(key: ResourceKey) {
    cache.value[key].fetchedAt = now()
    cache.value[key].locale = localeStore.currentLocale
  }

  function hasLoaded(key: ResourceKey) {
    const meta = cache.value[key]

    return meta.fetchedAt > 0 && meta.locale === localeStore.currentLocale
  }

  // Generic loader wrapper (per resource)
  async function loadResource(
    key: ResourceKey,
    loader: () => Promise<void>,
    opts?: { force?: boolean },
  ) {
    if (!opts?.force && isFresh(key)) return

    const existing = inFlight.get(key)
    if (existing) return existing

    loading.value[key] = true
    errors.value[key] = undefined

    const p = (async () => {
      try {
        await loader()
        markFetched(key)
      } catch (e) {
        errors.value[key] = e
        // Do not update fetchedAt on failure
      } finally {
        loading.value[key] = false
        inFlight.delete(key)
      }
    })()

    inFlight.set(key, p)
    return p
  }

  // ---- Loaders (uses ContentService)
  async function loadContent(opts?: { force?: boolean }) {
    // B) control per resource for the best cache behavior.
    await Promise.all([
      loadResource('pages', async () => {
        pages.value = await ContentService.pages()
      }, opts),

      loadResource('references', async () => { 
        references.value = await ContentService.references() 
      }, opts),
      
    ])
  }

  async function loadPosts(opts?: { force?: boolean }) {
    await loadResource('posts', async () => {
      posts.value = await ContentService.posts()
    }, opts)
  }

  async function loadTaxonomies(opts?: { force?: boolean }) {
    await loadResource('taxonomies', async () => {
      taxonomies.value = await ContentService.taxonomies()
    }, opts)
  }

  async function loadFaq(opts?: { force?: boolean }) {
    await loadResource('faq', async () => {
      faq.value = await ContentService.faq()
    }, opts)
  }

  async function loadAttachments(opts?: { force?: boolean }) {
    await loadResource('attachments', async () => {
      attachments.value = await ContentService.attachments()
    }, opts)
  }

  /**
   * Call this from layouts / route guards.
   * - default: will NOT refetch if cache is fresh
   * - sets global isLoading for fullscreen preloader
   */
  async function ensureContent(opts?: { force?: boolean }) {
    isLoading.value = true
    try {
      await Promise.all([
        loadContent(opts),
        loadTaxonomies(opts),
      ])

      if (!hasActiveErrors()) {
        ui.clearBackendUnavailable()
      }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Manual refresh button / pull-to-refresh (forces)
   */
  async function refreshAll() {
    await ensureContent({ force: true })
  }

  function invalidate(keys?: ResourceKey[]) {
    const list = keys ?? (Object.keys(cache.value) as ResourceKey[])
    for (const k of list) {
      cache.value[k].fetchedAt = 0
      cache.value[k].locale = null
    }
  }

  return {
    // data
    pages,
    posts,
    attachments,
    taxonomies,

    faq, 
    references, 
    
    // status
    isLoading,
    loading,
    errors,
    hasErrors,

    // cache helpers
    hasLoaded,
    ensureContent,
    refreshAll,
    invalidate,

    // direct actions
    loadContent,
    loadPosts,
    loadAttachments,
    loadFaq,
    loadTaxonomies,
  }
})
